import { useState, useEffect, useCallback } from 'react';
import { WebSocketService, WebSocketServiceCallbacks } from '../services/WebSocketService';
import { Transcription } from '../types';
import { APIEndpoints } from '../config/APIEndpoints';

export interface UseWebSocketReturn {
  isConnected: boolean;
  connectionState: string;
  connect: (backendUrl?: string, room?: string) => Promise<void>;
  disconnect: () => void;
  error: Error | null;
}

export const useWebSocket = (
  onTranscription: (transcription: Transcription) => void,
  onAIResponse?: (aiResponse: any) => void,
  onAIResponseStart?: (data: any) => void,
  onAIResponseChunk?: (data: any) => void,
  onAIResponseComplete?: (data: any) => void,
  onAIResponseError?: (data: any) => void,
  onDisconnect?: () => void // Added LiveKit disconnect callback
): UseWebSocketReturn => {
  const [service] = useState(() => new WebSocketService());
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState('disconnected');
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Set up callbacks
    const callbacks: WebSocketServiceCallbacks = {
      onTranscription,
      onAIResponse: onAIResponse || (() => {}),
      onAIResponseStart: onAIResponseStart || (() => {}),
      onAIResponseChunk: onAIResponseChunk || (() => {}),
      onAIResponseComplete: onAIResponseComplete || (() => {}),
      onAIResponseError: onAIResponseError || (() => {}),
      onConnectionChange: (connected: boolean) => {
        setIsConnected(connected);
        setConnectionState(connected ? 'connected' : 'disconnected');
        if (connected) {
          setError(null);
        }
      },
      onError: (error: Error) => {
        setError(error);
        console.error('WebSocket error:', error);
      },
      onDisconnect // Pass through the LiveKit disconnect callback
    };

    service.setCallbacks(callbacks);

    // Cleanup on unmount
    return () => {
      service.disconnect();
    };
  }, [service, onTranscription, onAIResponse, onAIResponseStart, onAIResponseChunk, onAIResponseComplete, onAIResponseError, onDisconnect]);

  const connect = useCallback(async (backendUrl?: string, room?: string) => {
    try {
      setError(null);
      setConnectionState('connecting');
      await service.connect(backendUrl || APIEndpoints.WEBSOCKET_URL, room);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to connect to WebSocket'));
      setConnectionState('disconnected');
      throw err;
    }
  }, [service]);

  const disconnect = useCallback(() => {
    service.disconnect();
    setConnectionState('disconnected');
    setIsConnected(false);
  }, [service]);

  return {
    isConnected,
    connectionState,
    connect,
    disconnect,
    error
  };
};
